import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Legal.css';

function Legal() {
    const { page } = useParams();
    const navigate = useNavigate();

    const isPrivacy = page === 'privacy';

    return (
        <div className="legal-container">
            <div className="legal-content">
                <button className="btn btn-secondary back-btn" onClick={() => navigate(-1)}>
                    ← Back
                </button>

                {isPrivacy ? <PrivacyPolicy /> : <TermsOfService />}

                <div className="legal-footer">
                    <p>Last updated: December 2024</p>
                    <p>Questions? Contact us at support@learnviva.com</p>
                </div>
            </div>
        </div>
    );
}

function PrivacyPolicy() {
    return (
        <>
            <h1>Privacy Policy</h1>

            <section>
                <h2>1. Information We Collect</h2>
                <p>When you use LearnViva, we collect:</p>
                <ul>
                    <li><strong>Account Information:</strong> Email address, display name, and profile picture (if using Google Sign-In)</li>
                    <li><strong>Learning Data:</strong> Your study progress, quiz scores, completed lessons, and learning preferences</li>
                    <li><strong>Usage Data:</strong> How you interact with the app, features used, and time spent learning</li>
                    <li><strong>Device Information:</strong> Browser type, device type, and operating system for improving compatibility</li>
                </ul>
            </section>

            <section>
                <h2>2. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul>
                    <li>Personalize your learning experience with AI-powered recommendations</li>
                    <li>Track your progress and provide achievement rewards</li>
                    <li>Generate customized study materials and flashcards</li>
                    <li>Improve our AI tutoring algorithms</li>
                    <li>Send important updates about your account (with your consent)</li>
                </ul>
            </section>

            <section>
                <h2>3. AI-Powered Features</h2>
                <p>LearnViva uses AI services (OpenAI and Anthropic Claude) to:</p>
                <ul>
                    <li>Generate personalized learning paths</li>
                    <li>Create interactive lessons and quizzes</li>
                    <li>Answer your questions in real-time</li>
                    <li>Provide voice-based tutoring</li>
                </ul>
                <p>Your learning queries are processed by these AI services but are not used to train their models. We do not share personally identifiable information with AI providers.</p>
            </section>

            <section>
                <h2>4. Data Storage and Security</h2>
                <p>Your data is stored securely using:</p>
                <ul>
                    <li><strong>Firebase/Google Cloud:</strong> For authentication and user data</li>
                    <li><strong>HTTPS encryption:</strong> All data transmitted is encrypted</li>
                    <li><strong>Access controls:</strong> Only you can access your learning data</li>
                </ul>
            </section>

            <section>
                <h2>5. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                    <li>Access your personal data</li>
                    <li>Correct inaccurate data</li>
                    <li>Delete your account and all associated data</li>
                    <li>Export your learning progress</li>
                    <li>Opt out of non-essential communications</li>
                </ul>
            </section>

            <section>
                <h2>6. Cookies and Tracking</h2>
                <p>We use essential cookies for:</p>
                <ul>
                    <li>Keeping you logged in</li>
                    <li>Remembering your preferences</li>
                    <li>Basic analytics to improve the app</li>
                </ul>
                <p>We do not use third-party advertising cookies.</p>
            </section>

            <section>
                <h2>7. Children's Privacy</h2>
                <p>LearnViva is designed for users of all ages. We do not knowingly collect personal information from children under 13 without parental consent. If you believe a child has provided us with personal information, please contact us.</p>
            </section>

            <section>
                <h2>8. Changes to This Policy</h2>
                <p>We may update this policy from time to time. We will notify you of significant changes via email or in-app notification.</p>
            </section>

            <section>
                <h2>9. Contact Us</h2>
                <p>For privacy concerns or data requests, contact us at:</p>
                <p><strong>Email:</strong> privacy@learnviva.com</p>
            </section>
        </>
    );
}

function TermsOfService() {
    return (
        <>
            <h1>Terms of Service</h1>

            <section>
                <h2>1. Acceptance of Terms</h2>
                <p>By accessing or using LearnViva, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.</p>
            </section>

            <section>
                <h2>2. Description of Service</h2>
                <p>LearnViva is an AI-powered learning platform that provides:</p>
                <ul>
                    <li>Personalized learning paths on various subjects</li>
                    <li>Interactive lessons with AI tutoring</li>
                    <li>Gamified learning experiences</li>
                    <li>Flashcard-based spaced repetition study</li>
                    <li>Voice-enabled mentor interactions</li>
                </ul>
            </section>

            <section>
                <h2>3. User Accounts</h2>
                <p>To use LearnViva, you must:</p>
                <ul>
                    <li>Create an account with accurate information</li>
                    <li>Keep your login credentials secure</li>
                    <li>Be at least 13 years old (or have parental consent)</li>
                    <li>Not share your account with others</li>
                </ul>
            </section>

            <section>
                <h2>4. Acceptable Use</h2>
                <p>You agree NOT to:</p>
                <ul>
                    <li>Use the service for any illegal purpose</li>
                    <li>Attempt to hack, reverse engineer, or disrupt the service</li>
                    <li>Use automated tools to scrape content</li>
                    <li>Share inappropriate content through the platform</li>
                    <li>Impersonate others or provide false information</li>
                    <li>Attempt to bypass rate limits or abuse AI features</li>
                </ul>
            </section>

            <section>
                <h2>5. AI-Generated Content</h2>
                <p>LearnViva uses AI to generate educational content. While we strive for accuracy:</p>
                <ul>
                    <li>AI-generated content may occasionally contain errors</li>
                    <li>Content is for educational purposes and should not replace professional advice</li>
                    <li>You should verify critical information from authoritative sources</li>
                </ul>
            </section>

            <section>
                <h2>6. Intellectual Property</h2>
                <p>The LearnViva platform, including its design, code, and features, is owned by us. Content you create (notes, custom study materials) remains yours. AI-generated content created for your learning is licensed for your personal educational use.</p>
            </section>

            <section>
                <h2>7. Service Availability</h2>
                <p>We aim for high availability but do not guarantee uninterrupted service. We may:</p>
                <ul>
                    <li>Perform maintenance with or without notice</li>
                    <li>Modify or discontinue features</li>
                    <li>Limit usage during high-demand periods</li>
                </ul>
            </section>

            <section>
                <h2>8. Limitation of Liability</h2>
                <p>LearnViva is provided "as is" without warranties. We are not liable for:</p>
                <ul>
                    <li>Learning outcomes or academic performance</li>
                    <li>Errors in AI-generated content</li>
                    <li>Service interruptions or data loss</li>
                    <li>Third-party actions or content</li>
                </ul>
            </section>

            <section>
                <h2>9. Termination</h2>
                <p>We may suspend or terminate your account if you violate these terms. You may delete your account at any time through your profile settings.</p>
            </section>

            <section>
                <h2>10. Changes to Terms</h2>
                <p>We may update these terms. Continued use after changes constitutes acceptance. Significant changes will be communicated via email or in-app notification.</p>
            </section>

            <section>
                <h2>11. Governing Law</h2>
                <p>These terms are governed by applicable laws. Any disputes will be resolved through good-faith negotiation or, if necessary, appropriate legal venues.</p>
            </section>

            <section>
                <h2>12. Contact</h2>
                <p>For questions about these terms, contact us at:</p>
                <p><strong>Email:</strong> legal@learnviva.com</p>
            </section>
        </>
    );
}

export default Legal;
